import { h, Host } from '@stencil/core';
import isEmpty from 'lodash/isEmpty';
import { gaLocationNames, virtualPagesNames, pesCatalogPageTypes, CR, triggerCtaClickEvent, isAnalyticsEnabled, QdsSize } from '@pes-ui/utils';
import { CO2Message } from './CO2Message';
import { isBrowser } from '../../../globals/utils';
import { ProductCtaContainer } from '../product-main/product-cta-container';
import { PesTag } from '../../reusable/pes-tag';
export class MainArea {
  constructor() {
    this.isApc = Boolean(window.isApc);
    this.renderAlternatives = () => {
      var _a;
      if (!((_a = this.alternatives) === null || _a === void 0 ? void 0 : _a.products)) {
        return null;
      }
      return (h("pes-substitutions-or-alternatives", { data: this.alternatives, productId: this.productId, class: "subsection__item", isApc: this.isApc, localeCode: this.localeCode }));
    };
    this.handleB2cRedirectionClick = (url) => {
      var _a, _b;
      if (!url || !isAnalyticsEnabled()) {
        console.warn('[PES]: B2C redirection URL is missing or analytics is disabled.');
        return;
      }
      const ctaNameValue = `${(_a = this.b2cRedirectionData) === null || _a === void 0 ? void 0 : _a.label} ${(_b = this.b2cRedirectionData) === null || _b === void 0 ? void 0 : _b.cta.label}`;
      triggerCtaClickEvent({
        cta_location: gaLocationNames[pesCatalogPageTypes.PRODUCT],
        cta_name: ctaNameValue,
        cta_link: url,
        cta_category: virtualPagesNames.BUY_ONLINE,
      });
    };
    this.productId = undefined;
    this.productCR = undefined;
    this.productInfo = undefined;
    this.productStatus = undefined;
    this.variants = undefined;
    this.selectorConfig = undefined;
    this.addToMyProductsLabel = undefined;
    this.removeFromMyProductsLabel = undefined;
    this.compareLabel = undefined;
    this.labels = undefined;
    this.showCompare = undefined;
    this.showFavorites = undefined;
    this.highlightedCharacteristics = undefined;
    this.additionalInfo = undefined;
    this.localeCode = undefined;
    this.isInFavorites = false;
    this.substitutions = undefined;
    this.alternatives = undefined;
    this.bulletPoints = undefined;
    this.bulletPointsLimit = undefined;
    this.sustainability = undefined;
    this.environmentalData = undefined;
    this.bazaarVoiceInfo = undefined;
    this.showProductSelectorStatus = undefined;
    this.circularEconomy = undefined;
    this.isCircularProductEnabled = false;
    this.isCircularProductAvailable = false;
    this.isStockAvailabilityChecked = false;
    this.activeProductTab = undefined;
    this.circularProductRelationships = undefined;
    this.isCircularProductLoading = false;
    this.co2message = undefined;
    this.showBuyOnline = undefined;
    this.ctaInfo = undefined;
    this.offlineStores = undefined;
    this.b2cRedirectionData = undefined;
    this.ctaContainerContent = undefined;
    this.tags = undefined;
    this.isExternalConsumer = undefined;
    this.externalUrlConfig = undefined;
    this.showProductSelector = this.isProductSelectorConfigured();
  }
  isProductSelectorConfigured() {
    var _a;
    if (((_a = this.selectorConfig) === null || _a === void 0 ? void 0 : _a.type) !== CR) {
      return true;
    }
    return false;
  }
  renderMainInfo() {
    return (h("pes-main-product-info", { class: "main-area__info", productInfo: this.productInfo, productCR: this.productCR }));
  }
  renderQuickActions() {
    return (h("pes-quick-actions", { class: "main-area__quick-actions", addToMyProductsLabel: this.addToMyProductsLabel, removeFromMyProductsLabel: this.removeFromMyProductsLabel, compareLabel: this.compareLabel, productId: this.productId, showCompare: this.showCompare, bazaarVoiceInfo: this.bazaarVoiceInfo, showFavorites: this.showFavorites, isInFavorites: this.isInFavorites }));
  }
  renderLifecycleInfo() {
    return h("pes-life-cycle-info", { productStatus: this.productStatus });
  }
  renderSubstitutions() {
    var _a, _b, _c;
    if (!((_b = (_a = this.substitutions) === null || _a === void 0 ? void 0 : _a.substitutions) === null || _b === void 0 ? void 0 : _b.length)) {
      if ((_c = this.substitutions) === null || _c === void 0 ? void 0 : _c.substitutionAbsentLabel) {
        return (h("pes-substitutions-not-found", { content: this.substitutions.substitutionAbsentLabel, class: "subsection__item" }));
      }
      return null;
    }
    return (h("pes-substitutions-or-alternatives", { data: this.substitutions, productId: this.productId, class: "subsection__item", isApc: this.isApc, isExternalConsumer: this.isExternalConsumer, externalUrlConfig: this.externalUrlConfig, localeCode: this.localeCode }));
  }
  get hasVariantsChars() {
    return (!!this.variants
      && (('chars' in this.variants && this.variants.chars.length > 0)
        || (Array.isArray(this.variants) && this.variants.length > 0)));
  }
  renderProductVariants() {
    if (this.hasVariantsChars) {
      return h("pes-pdp-product-variants", { variants: this.variants });
    }
    return null;
  }
  renderHighlightedCharacteristics() {
    if (isEmpty(this.highlightedCharacteristics)) {
      return null;
    }
    return (h("pes-highlighted-characteristics", { class: "subsection__item", highlightedCharacteristics: this.highlightedCharacteristics }));
  }
  renderAdditionalInfo() {
    if (!this.additionalInfo) {
      return null;
    }
    return (h("pes-additional-info", { class: "subsection__item", title: this.additionalInfo.title, version: this.additionalInfo.version, labels: this.additionalInfo.labels, os: this.additionalInfo.os, format: this.additionalInfo.format, size: this.additionalInfo.size, date: this.additionalInfo.date, language: this.additionalInfo.language }));
  }
  renderProductSelector() {
    if (this.isApc || !this.showProductSelectorStatus.loaded)
      return;
    return (h("pes-cta-product-selector", { selectorConfig: this.selectorConfig, showProductSelectorStatus: this.showProductSelectorStatus }));
  }
  subsectionEndsWithSubstitutions() {
    var _a, _b;
    if ((_b = (_a = this.substitutions) === null || _a === void 0 ? void 0 : _a.substitutions) === null || _b === void 0 ? void 0 : _b.length) {
      return !this.variants && !this.showProductSelector;
    }
    return false;
  }
  sectionEndsWithSubstitutions() {
    return (this.subsectionEndsWithSubstitutions()
      && isEmpty(this.highlightedCharacteristics)
      && !this.additionalInfo);
  }
  renderBulletPoints() {
    var _a, _b;
    if (!this.bulletPoints)
      return null;
    return (h("pes-bullet-points", { class: "subsection__item", data: this.bulletPoints, productId: this.productId, readMoreLabel: (_a = this.labels) === null || _a === void 0 ? void 0 : _a.readMoreLabel, readLessLabel: (_b = this.labels) === null || _b === void 0 ? void 0 : _b.readLessLabel, bulletPointsLimit: this.bulletPointsLimit }));
  }
  renderSustainabilityOverview() {
    if (!this.sustainability)
      return null;
    const { energyStarLogo, greenPremiumLogo, sustainabilityPillar } = this.sustainability;
    if (!energyStarLogo && !greenPremiumLogo && sustainabilityPillar.length === 0) {
      return null;
    }
    return (h("pes-sustainability-overview", { sustainability: this.sustainability, withAnchorLinks: true }));
  }
  render() {
    const { enableNewWidget } = this.selectorConfig || {};
    return (h(Host, { class: {
        'pes-main-area': true,
        'main-area--no-separator': this.sectionEndsWithSubstitutions(),
      } }, (!isBrowser()
      || !this.isCircularProductEnabled
      || (this.isStockAvailabilityChecked && !this.isCircularProductAvailable))
      ? (h("div", { class: "main-area-tabs-empty-container" })) : (h("pes-main-product-tabs", { circularEconomy: this.circularEconomy, circularProductRelationships: this.circularProductRelationships, isCircularProductAvailable: this.isCircularProductAvailable })), !this.isCircularProductLoading
      && this.tags
      && (h("div", { class: "pes-tags" }, Object.entries(this.tags).map(([key, value]) => (h(PesTag, { key: key, id: key, value: value, size: QdsSize.SMALL }))))), this.isCircularProductLoading ? (h("pes-main-area-skeleton", null)) : (h("div", null, this.renderMainInfo(), this.renderQuickActions(), h("slot", { name: "slot-price-cta-container" }), h("div", { class: "area__subsection" }, this.co2message && h(CO2Message, { co2message: this.co2message }), this.renderLifecycleInfo(), this.renderSubstitutions(), this.renderAlternatives(), this.renderProductVariants(), !enableNewWidget && this.renderProductSelector()), h(ProductCtaContainer, { productId: this.productId, localeCode: this.localeCode, b2cRedirectionData: this.b2cRedirectionData, showBuyOnline: this.showBuyOnline, ctaInfo: this.ctaInfo, offlineStores: this.offlineStores, ctaContainerContent: this.ctaContainerContent, handleB2cRedirectionClick: this.handleB2cRedirectionClick }), h("div", { class: { area__subsection: !this.subsectionEndsWithSubstitutions() } }, this.renderHighlightedCharacteristics(), this.renderAdditionalInfo(), this.renderBulletPoints(), h("pes-environmental-top-block", { environmentalData: this.environmentalData }))))));
  }
  static get is() { return "pes-main-area"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["main-area.scss"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["main-area.css"]
    };
  }
  static get properties() {
    return {
      "productId": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "product-id",
        "reflect": false
      },
      "productCR": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "product-c-r",
        "reflect": false
      },
      "productInfo": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TProductInfo",
          "resolved": "{ description?: string; badge?: TProductBadge; eNumberLabel?: string; eNumberCode?: string; brand?: string; }",
          "references": {
            "TProductInfo": {
              "location": "import",
              "path": "../main-product-info/types"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "productStatus": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "ProductStatus",
          "resolved": "{ __typename?: \"ProductStatus\"; commercialColor?: string; commercialTextColor?: string; commercialMessage?: string; preCommercial?: boolean; preSale?: LifecycleInfoBlock; sale?: LifecycleInfoBlock; service?: LifecycleInfoBlock; title?: string; }",
          "references": {
            "ProductStatus": {
              "location": "import",
              "path": "../../../generated/graphql"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "variants": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TProductVariants | TOldProductVariants",
          "resolved": "[{ title: string; dropdown: boolean; variants: TOldProductVariant[]; }] | { chars: [{ title: string; charId: string; dropdown: boolean; variants: TProductVariant[]; }]; groupId: string; }",
          "references": {
            "TProductVariants": {
              "location": "import",
              "path": "../product-variants/types"
            },
            "TOldProductVariants": {
              "location": "import",
              "path": "../product-variants/types"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "selectorConfig": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TScHpcProductSelectorConfig",
          "resolved": "Omit<TScProductSelectorConfig, \"type\"> & Omit<THpcProductSelectorConfig, \"type\"> & { type: \"hpc\" | \"cr\" | \"sc\"; }",
          "references": {
            "TScHpcProductSelectorConfig": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "addToMyProductsLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "add-to-my-products-label",
        "reflect": false
      },
      "removeFromMyProductsLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "remove-from-my-products-label",
        "reflect": false
      },
      "compareLabel": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "compare-label",
        "reflect": false
      },
      "labels": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "Record<string, string>",
          "resolved": "{ [x: string]: string; }",
          "references": {
            "Record": {
              "location": "global"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "showCompare": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "show-compare",
        "reflect": false
      },
      "showFavorites": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "show-favorites",
        "reflect": false
      },
      "highlightedCharacteristics": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "THighlightedCharacteristics",
          "resolved": "{ all: TLabelValues[]; }",
          "references": {
            "THighlightedCharacteristics": {
              "location": "import",
              "path": "../types"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "additionalInfo": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TAdditionalInfo",
          "resolved": "{ title: string; version?: string; labels: Record<string, string>; os: string; format: string; size: string; date: string; language: string; }",
          "references": {
            "TAdditionalInfo": {
              "location": "import",
              "path": "../pes-additional-info/types"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "localeCode": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "locale-code",
        "reflect": false
      },
      "isInFavorites": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "is-in-favorites",
        "reflect": false,
        "defaultValue": "false"
      },
      "substitutions": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "SubstitutionInfo",
          "resolved": "{ __typename?: \"SubstitutionInfo\"; productSimpleLabel?: string; productSubstitutionDateLabel?: string; productSubstitutionReasonLabel?: string; qtyLabel?: string; viewAllLabel?: string; substitutions?: Substitution[]; title?: string; recommendedReplacementsLabel?: string; viewMoreLabel?: string; viewLessLabel?: string; addToMyProductsLabel?: string; removeFromMyProductsLabel?: string; addGroupToMyProductsLabel?: string; removeGroupFromMyProductsLabel?: string; productsNumberLabel?: string; substitutionAbsentLabel?: string; orLabel?: string; cartTransferEnabled?: boolean; }",
          "references": {
            "SubstitutionInfo": {
              "location": "import",
              "path": "../../../generated/graphql"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "alternatives": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "Alternatives",
          "resolved": "AlternativesInfo & { addToMyProductsLabel?: string; removeFromMyProductsLabel?: string; recommendedReplacementsLabel?: string; title?: string; alternativeAbsentLabel?: string; cartTransferEnabled?: boolean; viewMoreLabel?: string; viewLessLabel?: string; ctaInfo?: TCtaInfo; }",
          "references": {
            "Alternatives": {
              "location": "import",
              "path": "../../../generated/graphql"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "bulletPoints": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TBulletPointItem[]",
          "resolved": "TBulletPointItem[]",
          "references": {
            "TBulletPointItem": {
              "location": "import",
              "path": "../bullet-points/types"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "bulletPointsLimit": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "bullet-points-limit",
        "reflect": false
      },
      "sustainability": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TSustainabilityItem",
          "resolved": "{ greenPremiumLogo?: TGreenPremiumLogo; energyStarLogo?: TEnergyStarLogo; sustainabilityLabel?: string; seeAllLabel?: string; closeLabel?: string; sustainabilityPillar?: TSustainabilityPillar[]; }",
          "references": {
            "TSustainabilityItem": {
              "location": "import",
              "path": "../sustainability-overview/types"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "environmentalData": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TEnvironmentalTopBlock",
          "resolved": "{ viewFullEnvironmentalData: string; data: TEnvironmentalTopBlockDataInfo; }",
          "references": {
            "TEnvironmentalTopBlock": {
              "location": "import",
              "path": "../../product/environmental-top-block/types"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "bazaarVoiceInfo": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TBazaarVoiceInfo",
          "resolved": "{ enabled: boolean; scriptUrl: string; }",
          "references": {
            "TBazaarVoiceInfo": {
              "location": "import",
              "path": "../bazaar-voice-rating/types"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "showProductSelectorStatus": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TProductSelectorStatus",
          "resolved": "{ loaded: boolean; isExternalState: boolean; state: boolean; }",
          "references": {
            "TProductSelectorStatus": {
              "location": "import",
              "path": "../../../globals/types"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "circularEconomy": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "CircularEconomyNew",
          "resolved": "{ apiEndPointUrl: string; apiKey?: string; circularNatureLabels: Record<CircularProductTypes, string>; stockQuantityLabel?: string; newBadge: boolean; newBadgeLabel?: string; }",
          "references": {
            "CircularEconomyNew": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "isCircularProductEnabled": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "is-circular-product-enabled",
        "reflect": false,
        "defaultValue": "false"
      },
      "isCircularProductAvailable": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "is-circular-product-available",
        "reflect": false,
        "defaultValue": "false"
      },
      "isStockAvailabilityChecked": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "is-stock-availability-checked",
        "reflect": false,
        "defaultValue": "false"
      },
      "activeProductTab": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "active-product-tab",
        "reflect": false
      },
      "circularProductRelationships": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "CircularProductRelationships",
          "resolved": "CircularProductRelationship[]",
          "references": {
            "CircularProductRelationships": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "isCircularProductLoading": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "is-circular-product-loading",
        "reflect": false,
        "defaultValue": "false"
      },
      "co2message": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "co-2message",
        "reflect": false
      },
      "showBuyOnline": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "show-buy-online",
        "reflect": false
      },
      "ctaInfo": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "ProductsApi.TCtaInfo[]",
          "resolved": "TCtaInfo[]",
          "references": {
            "ProductsApi": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "offlineStores": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "OfflineStores",
          "resolved": "{ timeLabel?: string; time?: string; legalText?: string; findResellerLink?: FindResellerLink; }",
          "references": {
            "OfflineStores": {
              "location": "import",
              "path": "../pes-offline-stores/types"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "b2cRedirectionData": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "B2CRedirectionData",
          "resolved": "{ label: string; cta: { label: string; type?: string; url: string; }; }",
          "references": {
            "B2CRedirectionData": {
              "location": "import",
              "path": "../product-main/types"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "ctaContainerContent": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "Element",
          "resolved": "Element",
          "references": {
            "Element": {
              "location": "global"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "tags": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "PesTagsData",
          "resolved": "{ [key: string]: { label: string; }; }",
          "references": {
            "PesTagsData": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": ""
        }
      },
      "isExternalConsumer": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Defines consumer is external-user"
        },
        "attribute": "is-external-consumer",
        "reflect": false
      },
      "externalUrlConfig": {
        "type": "unknown",
        "mutable": false,
        "complexType": {
          "original": "TExternalUrlConfig",
          "resolved": "{ viewDetail: string[]; substitution: string[]; }",
          "references": {
            "TExternalUrlConfig": {
              "location": "import",
              "path": "../../../globals/external-consumer"
            }
          }
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Defines customized url(s) of hyperlink for external-user"
        }
      }
    };
  }
  static get states() {
    return {
      "showProductSelector": {}
    };
  }
}
